import { execAdmin } from '@src/apis/reware/utils';

import type {
  PricingHistoryEntry,
  PricingType,
  FetchPricingHistoryExecVariables,
  FetchPricingHistoryExecResponse,
} from './types';

export interface FetchPricingHistoryArgs {
  partnerId: number;
  pricingType: PricingType;
}

/**
 * Fetches the list of past pricing-rule snapshots for a partner + pricing type.
 *
 * Each entry is one prior save (initiated/processed timestamps and the user who
 * triggered it). The list powers the "History" view on the Condition Discounts
 * page, where each row offers a TSV download of that snapshot's rules.
 */
export const fetchPricingHistory = async ({
  partnerId,
  pricingType,
}: FetchPricingHistoryArgs): Promise<PricingHistoryEntry[]> => {
  const query = `
    query getPricingHistory($partnerId: Int!, $pricingType: String!) {
      partner(id: $partnerId) {
        pricingHistory(pricingType: $pricingType) {
          id
          partnerId
          userId
          createdAt
          initiatedAt
          processedAt
          user {
            id
            name
          }
        }
      }
    }
  `;

  const data = await execAdmin<FetchPricingHistoryExecVariables, FetchPricingHistoryExecResponse>(
    query,
    { variables: { partnerId, pricingType } },
  );

  const history = data.partner?.pricingHistory;
  if (!history) {
    throw new Error(
      `fetchPricingHistory: unexpected response — partner.pricingHistory is ${
        history === null ? 'null' : 'undefined'
      } (partnerId=${partnerId}, pricingType=${pricingType})`,
    );
  }

  // The generated schema models elements as `Maybe<PricingDiscountHistoryAdmin>`.
  // Strip nulls so the caller (Datagrid renderer) can rely on `entry.id`/`createdAt`.
  return history.filter((entry): entry is PricingHistoryEntry => entry != null);
};
